import React from 'react';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
//
// Define the interface for each item:
//
interface DifferenceItem {
    icon: JSX.Element;
    title: string;
    paragraph: string;
}

//
// Define the props for the BitBlossomDifference component.
//
interface BitBlossomDifferenceProps {
    header?: string;
    items: DifferenceItem[];
    imageSrc : string;
    longerText?: boolean;
}

//
// Define your styles
//
const h1Style = {
    fontSize: {
        xs: '24px',
        sm: '24px',
        md: '40px',
    },
    fontFamily: 'Signika',
    color: 'primary.main',
    lineHeight: {
        xs: 1,
        md: 1.5,
        lg: 1.5,
    },
};

const h2Style = {
    fontSize: {
        xs: '14px',
        sm: '14px',
        md: '20px',
    },
    fontFamily: 'DM Sans',
    lineHeight: {
        xs: 1,
        md: 1.5,
        lg: 1.5,
    },
    fontWeight: 'bold',
    color: 'primary.main',
    mt: { xs: 0, lg: 1 },
    mb: { xs: 1, lg: 1 },
};

const bodyStyle = {
    fontFamily: 'DM Sans',
    fontSize: {
        xs: '14px',
        sm: '14px',
        md: '20px',
    },
    lineHeight: { xs: 1, lg: 1.8 },
};

const iconSizing = {
    fontSize: {
        xs: '20px',
        sm: '20px',
        md: '24px',
        lg: '35px',
    },
    color: 'primary.main',
    mt: { xs: 0, lg: 0 },
    mb: { xs: 0, lg: 0 },
};

//
// The updated component
//
const BitBlossomDifference: React.FC<BitBlossomDifferenceProps> = ({
    header = 'The Bit Blossom Difference',
    items,
    imageSrc,
    longerText = false,
}) => {
    const mb = longerText ? 1 : 5;

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'visible',
                width: '100%',
                mt: { xs: 5, md:7, lg: 10 },
            }}
        >
            {/* Background Image */}
            <Box
                component="img"
                src="/ContactUsBackground.jpg"
                alt="Bit Blossom Difference"
                sx={{
                    display: 'block',
                    width: {xs:'auto',sm:'100%'},
                    height: {xs:'550px' ,sm:'auto'},
                    objectFit: 'cover',
                    opacity: '60%',
                }}
            />

            <Box
                sx={{
                    position: 'absolute',
                    top: { xs: 25, md:25, lg: 50 },
                    right: { xs: 25, md:25, lg: 50 },
                    width: '90%',
                    height: { xs:'500px',sm: longerText ? '90%' : '85%', md : longerText ? '90%' : '85%', lg: longerText ? '90%' : '85%' },
                    backgroundColor: 'white',
                    display: 'flex',
                    flexDirection: 'row',
                    padding: 2,
                    overflow: 'visible',
                }}
            >
                <Box
                    component="img"
                    src="/BitBlossom_Logo_Badge.png"
                    alt="Bit Blossom Badge"
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        ml: { xs: -2, md:-3, lg: -5 },
                        mt: { xs: -1, md:-2, lg: -2 },
                        width: { xs: 50, md:100, lg: 150 },
                    }}
                />
                <Box
                    sx={{
                        flexGrow: 1,
                        flexShrink: 1,
                        flexBasis: { xs: '30%', md:'35%', lg: '40%' },
                        padding: { xs: 0, md:1, lg: 2 },
                        display: {xs:'none', sm:'flex'},
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        component="img"
                        src={imageSrc}
                        alt="Bit Blossom Difference"
                        sx={{
                            maxHeight: '100%',
                            maxWidth: '100%',
                            objectFit: 'contain',
                        }}
                    />
                </Box>
                <Box
                    sx={{
                        flexGrow: 1,
                        flexShrink: 1,
                        flexBasis: { xs: '70%', md:'65%', lg: '60%' },
                        padding: 2,
                        mt: { xs: 0, md:2, lg: 3 },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        textAlign: 'center',
                    }}
                >
                    <Grid container spacing={{ xs: 1, md:1, lg: 2 }}>
                        <Grid
                            size={{ xs: 12 }}
                            sx={{ display: 'flex', flexDirection: {xs:'row', sm: 'column'}, alignItems: 'center', justifyContent: 'center' }}
                        >
                            <Typography sx={{ ...h1Style, mb: { xs: 1, md:1, lg: 2 } }}>{header}</Typography>
                        </Grid>

                        {items.map((item, index) => (
                            <Grid
                                key={index}
                                size={{ xs: 12,sm:6, md:6 }}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    // (Optional: adjust marginBottom per your design)
                                    mb: { xs: 1, md:index % 2 === 1 ? mb : 0,  lg: index % 2 === 1 ? mb : 0 },
                                }}
                            >
                                {/* Clone the icon element so we can pass in the iconSizing sx prop */}
                                {React.cloneElement(item.icon, { sx: iconSizing })}
                                <Typography sx={{ ...h2Style, color: 'primary.main' }}>{item.title}</Typography>
                                <Typography sx={{ ...bodyStyle, color: 'secondary.main' }}>
                                    {item.paragraph}
                                </Typography>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
};

export default BitBlossomDifference;
